import { BarChart2 } from 'react-feather';
import { ChangeEvent } from 'react';
import { DocumentNode } from 'graphql';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { ExportFormat, OrderFilters } from '__generated__/graphql';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import ExportOrdersMutation from './ExportOrdersMutation';
import ExportPurchasesMutation from './ExportPurchasesMutation';
import GetExportProfilesQuery from '../../Exports/GetExportProfilesQuery';
import GetExportsQuery from '../../Exports/GetExportsQuery';
import UI from '../../../common/UI';
import config from '../../config';
import useLocale from '../../../common/useLocale';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';

export interface ExportOrdersFormProps {
  filters: OrderFilters;
  ordersCount: number;
  purchasesCount: number;
  onSuccess?: () => void;
  refetchQueries?: DocumentNode[];
}

const ExportOrdersForm = ({
  filters, ordersCount, purchasesCount, onSuccess,
  refetchQueries = [GetExportsQuery],
}: ExportOrdersFormProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate, formatNumber } = useLocale();
  const project = useProject();
  const notifier = useNotifier();

  const eventId = (filters.events || []).length === 1 ? filters.events[0] : null;

  const form = useForm({
    values: {
      type: ordersCount <= config.maxExportSize.orders ? 'orders' : undefined,
      export_profile_type: 'none',
      export_profile: null,
      columns: [],
      format: ExportFormat.excel,
    },
  });

  const handleExportProfileTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;

    if (value === 'none') {
      form.set((values) => ({
        ...values,
        export_profile_type: 'none',
        export_profile: null,
      }));
    } else if (value === 'create_new') {
      form.set((values) => ({
        ...values,
        export_profile_type: 'create_new',
        export_profile: {
          title: '',
        },
      }));
    } else if (value) {
      form.set((values) => ({
        ...values,
        export_profile_type: value,
        export_profile: {
          id: value,
        },
      }));
    }
  };

  const enabledColumns = form.values.columns.reduce(
    (values, { column, title, enabled }) => (enabled ? [...values, { column, title }] : values),
    [],
  );

  const input = {
    project: project.id,
    export_profile: form.values.export_profile,
    columns: enabledColumns,
    filters,
    options: {
      format: form.values.format,
    },
  };

  const [exportOrders, ordersResult] = useMutation(
    ExportOrdersMutation,
    {
      variables: {
        input,
      },
      onCompleted: () => {
        notifier.success(t('notifications.export_started'));
        onSuccess?.();
      },
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  const [exportPurchases, purchasesResult] = useMutation(
    ExportPurchasesMutation,
    {
      variables: {
        input: {
          ...input,
          filters: {
            // Map order filters to purchase filters.
            active: true,
            assigned: input.filters.purchase_filters?.assigned,
            coupons: input.filters.purchase_filters?.coupons,
            discounts: input.filters.purchase_filters?.discounts,
            invitations: input.filters.purchase_filters?.invitations,
            payment_statuses: input.filters.purchase_filters?.payment_statuses,
            product_variants: input.filters.purchase_filters?.product_variants,
            products: input.filters.purchase_filters?.products,
            promotions: input.filters.purchase_filters?.promotions,
            ticket_categories: input.filters.purchase_filters?.ticket_categories,
            tickets: input.filters.purchase_filters?.tickets,
            time_slots: input.filters.purchase_filters?.time_slots,
            order_filters: {
              events: input.filters.events,
              search: input.filters.search,
              participant_field_choices: input.filters.participant_field_choices,
              participant_fields: input.filters.participant_fields,
              participant_filters: {
                locales: input.filters.participant_filters?.locales,
                mail_opt_ins: input.filters.participant_filters?.mail_opt_ins,
              },
            },
          },
        },
      },
      onCompleted: () => {
        notifier.success(t('notifications.export_started'));
        onSuccess?.();
      },
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  const {
    data: exportProfilesData,
  } = useQuery(
    GetExportProfilesQuery,
    {
      variables: {
        project_id: project.id,
        type: form.values.type,
        event_id: eventId,
      },
      skip: !form.values.type,
      onCompleted: (data) => {
        form.reset((values) => ({
          ...values,
          columns: data.project.default_export_columns.map((column) => ({
            column: column.id,
            title: '',
            enabled: false,
          })),
        }));
      },
    },
  );

  const selectedExportProfile = exportProfilesData?.project.export_profiles.filter(
    (exportProfile) => exportProfile.id === form.values.export_profile?.id,
  )[0];

  const handleSelectAllColumns = () => {
    form.set((values) => ({
      ...values,
      columns: form.values.columns.map((value) => ({ ...value, enabled: true })),
    }));
  };

  const handleDeselectAllColumns = () => {
    form.set((values) => ({
      ...values,
      columns: form.values.columns.map((value) => ({ ...value, enabled: false })),
    }));
  };

  const allColumnsSelected = form.values.columns.filter((value) => !value.enabled).length === 0;
  const noColumnsSelected = form.values.columns.filter((value) => value.enabled).length === 0;

  const loading = ordersResult?.loading || purchasesResult?.loading;
  const error = ordersResult?.error || purchasesResult?.error;

  const handleSubmit = () => {
    if (form.values.type === 'orders') {
      exportOrders();
    } else if (form.values.type === 'purchases') {
      exportPurchases();
    }
  };

  return (
    <UI.Form onSubmit={handleSubmit}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('export_orders')}
          </UI.H4>
          <UI.Paragraph sc={{ mb: 0 }}>
            {t('export_orders_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.HR />

        <UI.ServerErrors error={error} />

        <UI.FormGrid sc={{ gutter: 0.25 }}>
          <UI.Radio
            {...form.radio('type', 'orders', { onChange: () => form.reset('export_profile_type') })}
            disabled={ordersCount > config.maxExportSize.orders}
            sc={{ box: true }}
            style={{ opacity: 1 }}
          >
            <UI.Div sc={{ muted: ordersCount > config.maxExportSize.orders }}>
              <UI.Strong sc={{ block: true }}>
                {t('orders')}
              </UI.Strong>
              {t('orders_export_description')}
            </UI.Div>
            {ordersCount > config.maxExportSize.orders && (
              <UI.Warning sc={{ mt: 1 }}>
                {`${t('export_too_large')} (${formatNumber(ordersCount)} / ${formatNumber(config.maxExportSize.orders)})`}
              </UI.Warning>
            )}
          </UI.Radio>
          <UI.Radio
            {...form.radio('type', 'purchases', { onChange: () => form.reset('export_profile_type') })}
            disabled={purchasesCount > config.maxExportSize.purchases}
            sc={{ box: true }}
            style={{ opacity: 1 }}
          >
            <UI.Div sc={{ muted: purchasesCount > config.maxExportSize.purchases }}>
              <UI.Strong sc={{ block: true }}>
                {t('purchases')}
              </UI.Strong>
              {t('purchases_export_description')}
            </UI.Div>
            {purchasesCount > config.maxExportSize.purchases && (
              <UI.Warning sc={{ mt: 1 }}>
                {`${t('export_too_large')} (${formatNumber(purchasesCount)} / ${formatNumber(config.maxExportSize.purchases)})`}
              </UI.Warning>
            )}
          </UI.Radio>
        </UI.FormGrid>

        <UI.InputGroup>
          <UI.InputLabel htmlFor="export_profile_type">
            {t('export_profile')}
          </UI.InputLabel>
          <UI.Select
            value={form.values.export_profile_type}
            onChange={handleExportProfileTypeChange}
            id="export_profile_type"
          >
            <option value="none">
              {t('all_columns')}
            </option>
            <option value="create_new">
              {t('custom_columns')}
            </option>
            {exportProfilesData?.project.export_profiles.map((exportProfile) => (
              <option value={exportProfile.id} key={exportProfile.id}>
                {exportProfile.title}
                {' • '}
                {t('n_columns', {
                  value: formatNumber(exportProfile.column_titles.length),
                  count: exportProfile.column_titles.length,
                })}
                {' • '}
                {formatDate(parseDate(exportProfile.created_at), { format: 'display_compact_date' })}
              </option>
            ))}
          </UI.Select>
        </UI.InputGroup>

        {form.values.export_profile_type === 'create_new' && (
          <UI.InputGroup>
            <UI.InputLabel>
              {t('columns')}
              <UI.Span sc={{ muted: true }}> • </UI.Span>
              <UI.A
                onClick={() => handleSelectAllColumns()}
                sc={{ disabled: allColumnsSelected }}
              >
                {t('select_all')}
              </UI.A>
              <UI.Span sc={{ muted: true }}> • </UI.Span>
              <UI.A
                onClick={() => handleDeselectAllColumns()}
                sc={{ disabled: noColumnsSelected }}
              >
                {t('deselect_all')}
              </UI.A>
            </UI.InputLabel>
            <UI.InputDescription>
              {t('create_export_profile_description')}
            </UI.InputDescription>
            <UI.FormGrid sc={{ gutter: 0.0625 }}>
              {form.values.columns.map((value, index) => {
                const column = exportProfilesData?.project.default_export_columns[index];

                return (
                  <UI.FormGrid
                    sc={{
                      columns: '1fr 1fr',
                      gutter: 0.25,
                      background: 'gray.75',
                    }}
                    key={value.column}
                  >
                    <UI.Div style={{ minWidth: 0 }}>
                      <UI.Checkbox
                        {...form.checkbox(`columns.${index}.enabled`)}
                        sc={{
                          fontWeight: form.values.columns[index].enabled ? 500 : undefined,
                          pl: 6,
                          py: 2.5,
                          checkboxLeft: 12,
                          checkboxTop: 19,
                        }}
                      >
                        <UI.Truncate>
                          {column.title}
                        </UI.Truncate>
                      </UI.Checkbox>
                    </UI.Div>
                    <UI.Div sc={{ py: 1, pr: 2 }}>
                      <UI.Input
                        {...form.text(`columns.${index}.title`, { onChange: () => form.set(`columns.${index}.enabled`, true) })}
                        sc={{ size: 'md' }}
                        placeholder={column.title}
                      />
                    </UI.Div>
                  </UI.FormGrid>
                );
              })}
            </UI.FormGrid>
          </UI.InputGroup>
        )}

        {selectedExportProfile && (
          <UI.Info>
            <UI.Delimit>
              {selectedExportProfile.column_titles}
            </UI.Delimit>
          </UI.Info>
        )}

        {form.values.export_profile_type === 'create_new' && (
          <UI.InputGroup>
            <UI.InputLabel htmlFor="add_export_profile_title">
              {t('export_profile_title')}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('export_profile_title_description')}
            </UI.InputDescription>
            <UI.Input {...form.text('export_profile.title')} id="add_export_profile_title" />
          </UI.InputGroup>
        )}

        <UI.InputGroup>
          <UI.InputLabel>
            {t('file_format')}
          </UI.InputLabel>
          <UI.Radio {...form.radio('format', ExportFormat.excel)}>
            {t('export_formats.excel')}
          </UI.Radio>
          <UI.Radio {...form.radio('format', ExportFormat.csv)}>
            {t('export_formats.csv')}
          </UI.Radio>
        </UI.InputGroup>

        <UI.HR />

        <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <UI.Div>
            <UI.Icon>
              <BarChart2 />
            </UI.Icon>
            {' '}
            {t('number_of_rows')}
            {': '}
            {formatNumber(form.values.type === 'orders' ? ordersCount : purchasesCount)}
          </UI.Div>
          <UI.Button
            type="submit"
            disabled={loading || (form.values.export_profile_type === 'create_new' && enabledColumns.length === 0)}
            sc={{ brand: 'secondary' }}
          >
            {t('export')}
          </UI.Button>
        </UI.FlexContainer>
      </UI.FormGrid>
    </UI.Form>
  );
};

export default ExportOrdersForm;
